// ═══════════════════════════════════════════════════════════════════
//  VNEC v9 — Naming Rules Helper (port từ V8)
//  Gọi các RPC Supabase để sinh mã HĐ, PL, ĐH, PYC
//  File: frontend/src/lib/naming.js
// ═══════════════════════════════════════════════════════════════════

import { supabase } from './supabase.js';

// ─────────────────────────────────────────────────────────────────
// 1. MÃ HỢP ĐỒNG — Format: {STT}/VNEC_{MaNTP}/{PhanLoai}/{HinhThuc}/{MaDuAn}
// ─────────────────────────────────────────────────────────────────

/**
 * Build mã HĐ client-side từ các thành phần + STT.
 * @param {object} parts { stt, maNTP, phanLoai, hinhThuc, maDuAn }
 * @returns {string} mã HĐ đầy đủ
 */
export function buildMaHD({ stt, maNTP, phanLoai, hinhThuc, maDuAn }) {
  return [
    stt ?? '...',
    'VNEC_' + (maNTP || '...'),
    phanLoai || '...',
    hinhThuc || '...',
    maDuAn || '...'
  ].join('/');
}

/**
 * Lấy STT tiếp theo cho HĐ gốc (gọi RPC Supabase).
 * @returns {Promise<number>}
 */
export async function getNextContractNumber() {
  const { data, error } = await supabase.rpc('get_next_contract_number');
  if (error) throw new Error('Không lấy được STT HĐ: ' + error.message);
  return Number(data) || 1;
}

/**
 * Preview mã HĐ trước khi save (cho UI live-update khi chọn dropdown).
 */
export async function previewMaHD({ maNTP, phanLoai, hinhThuc, maDuAn }) {
  const stt = await getNextContractNumber();
  return buildMaHD({ stt, maNTP, phanLoai, hinhThuc, maDuAn });
}

// ─────────────────────────────────────────────────────────────────
// 2. MÃ PHỤ LỤC HĐ — Format: {MaHDGoc}/PL-{NNN}
// ─────────────────────────────────────────────────────────────────

/**
 * Lấy mã phụ lục tiếp theo cho một HĐ gốc.
 * @param {string} maHDGoc
 * @returns {Promise<string>} ví dụ "12/VNEC_NTP001/.../PL-003"
 */
export async function getNextMaPL(maHDGoc) {
  if (!maHDGoc) throw new Error('Thiếu mã HĐ gốc');
  const { data, error } = await supabase.rpc('get_next_pl_suffix', {
    p_ma_hd_goc: maHDGoc
  });
  if (error) throw new Error('Không lấy được suffix PL: ' + error.message);
  const suffix = String(data || '001');
  return `${maHDGoc}/PL-${suffix}`;
}

// ─────────────────────────────────────────────────────────────────
// 3. MÃ ĐƠN HÀNG (HĐ Nguyên tắc) — Format: {MaHDKhung}/DH-{NNN}
// ─────────────────────────────────────────────────────────────────

/**
 * Lấy mã đơn hàng tiếp theo cho HĐ khung (Nguyên tắc).
 * @param {string} maHDKhung
 * @returns {Promise<string>}
 */
export async function getNextMaDH(maHDKhung) {
  if (!maHDKhung) throw new Error('Thiếu mã HĐ khung');
  const { data, error } = await supabase.rpc('get_next_dh_suffix', {
    p_ma_hd_khung: maHDKhung
  });
  if (error) throw new Error('Không lấy được suffix ĐH: ' + error.message);
  const suffix = String(data || '001');
  return `${maHDKhung}/DH-${suffix}`;
}

/**
 * Check HĐ có phải loại "Nguyên tắc" không (bỏ dấu, case-insensitive).
 * Accept cả "Nguyên tắc" (cũ) lẫn "HDNT" (mới).
 * Port từ V8 `_isNguyenTac_()`.
 */
export function isNguyenTac(val) {
  if (!val) return false;
  const v = String(val).toLowerCase().trim()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]/g, '');
  return v.indexOf('nguyentac') !== -1 || v === 'hdnt';
}

// ─────────────────────────────────────────────────────────────────
// 4. SỐ PHIẾU PYC — 3 loại quy tắc khác nhau
// ─────────────────────────────────────────────────────────────────
//
// a) thanh_toan: USER TỰ NHẬP, không auto-gen
// b) tam_ung:    TamUng|{N}|{MaHD}
// c) ung_quy:    UngQuy|{N}|{MaDuAn}  (default "CHUNG")
// ─────────────────────────────────────────────────────────────────

/**
 * Lấy số phiếu PYC tiếp theo (chỉ cho tam_ung / ung_quy).
 * @param {object} p { loaiPYC, maHD, maDuAn }
 * @returns {Promise<string>}
 */
export async function getNextSoPhieuPYC({ loaiPYC, maHD, maDuAn }) {
  if (loaiPYC === 'thanh_toan') {
    throw new Error('PYC Thanh toán phải nhập số phiếu thủ công');
  }
  if (loaiPYC !== 'tam_ung' && loaiPYC !== 'ung_quy') {
    throw new Error('Loại PYC không hợp lệ: ' + loaiPYC);
  }
  if (loaiPYC === 'tam_ung' && !maHD) {
    throw new Error('PYC Tạm ứng phải có mã HĐ');
  }

  const { data, error } = await supabase.rpc('get_next_pyc_so_phieu', {
    p_loai: loaiPYC,
    p_ma_hd: maHD || null,
    p_ma_du_an: maDuAn || null
  });
  if (error) throw new Error('Không lấy được số phiếu PYC: ' + error.message);
  return String(data);
}

/**
 * Parse số phiếu PYC để lấy các thành phần.
 * @param {string} soPhieu
 * @returns {{ loai, n, suffix } | null}
 */
export function parseSoPhieuPYC(soPhieu) {
  if (!soPhieu) return null;
  const parts = String(soPhieu).split('|');
  if (parts.length < 3) return null;  // thanh_toan không theo format này
  const prefix = parts[0];
  const loai = prefix === 'TamUng' ? 'tam_ung'
             : prefix === 'UngQuy' ? 'ung_quy'
             : null;
  if (!loai) return null;
  return {
    loai,
    n: parseInt(parts[1], 10) || 0,
    suffix: parts.slice(2).join('|')  // MaHD có thể chứa "|"? không, nhưng safe
  };
}

// ─────────────────────────────────────────────────────────────────
// 5. VALIDATION HELPERS
// ─────────────────────────────────────────────────────────────────

/**
 * Check mã HĐ đã tồn tại (loại trừ bản ghi đang edit).
 * @returns {Promise<boolean>}
 */
export async function isMaHDExist(maHD, excludeId = null) {
  // v9: column is so_hd (not ma_hd)
  let q = supabase.from('hop_dong').select('id', { head: true, count: 'exact' })
    .eq('so_hd', maHD).is('deleted_at', null);
  if (excludeId) q = q.neq('id', excludeId);
  const { count, error } = await q;
  if (error) throw new Error('Lỗi check trùng mã HĐ: ' + error.message);
  return (count || 0) > 0;
}

/**
 * Check số phiếu PYC đã tồn tại ở trạng thái active (Pending/Approved_L1/Approved).
 * Draft/Rejected được phép trùng để sửa.
 */
export async function isSoPhieuExist(soPhieu) {
  // v9: table is pyctt (not pyc_thanh_toan), column so_pyctt (not so_phieu),
  // states are lowercase enum
  const { count, error } = await supabase.from('pyctt')
    .select('id', { head: true, count: 'exact' })
    .eq('so_pyctt', soPhieu)
    .in('trang_thai', ['pending_l1', 'pending_l2', 'approved'])
    .is('deleted_at', null);
  if (error) throw new Error('Lỗi check trùng số phiếu: ' + error.message);
  return (count || 0) > 0;
}

/**
 * Check HĐ đã quyết toán chưa (có chi_phi với dot='QT').
 * Dùng để block tạo PYC/CP mới.
 */
export async function isHDQuyetToan(maHD) {
  // v9: chi_phi.hop_dong_id is UUID FK (not ma_hd text).
  // dot='QT' column added by 006_v9_fixes.sql.
  const { data: hd } = await supabase.from('hop_dong')
    .select('id').eq('so_hd', maHD).is('deleted_at', null).maybeSingle();
  if (!hd) return false;
  const { count, error } = await supabase.from('chi_phi')
    .select('id', { head: true, count: 'exact' })
    .eq('hop_dong_id', hd.id).eq('dot', 'QT').is('deleted_at', null);
  if (error) throw new Error('Lỗi check QT: ' + error.message);
  return (count || 0) > 0;
}
